import { useTranslation } from 'react-i18next'
import AccountCard from './account-card'
import { useFormatNumber } from '../../../../../core/hooks/useFormatNumber'
import { useContext, useEffect, useState } from 'react'
import { StoreTrajectoryContext } from '../../state/store-trajectory-context'
import {
  VpCircularityBuyBackIcon,
  VpCircularityMaintainRepairIcon,
  VpCircularitySecondLifeIcon,
  VpCircularityShopRentalIcon
} from '@vtmn-play/icons/react'

export const sumSalesChannelPercents = (percents: CircularPercents) =>
  Math.round(
    ((percents.repairAndMaintenance ?? 0) +
      (percents.savedProducts ?? 0) +
      (percents.buyBack ?? 0) +
      (percents.rental ?? 0)) *
      10
  ) / 10

export interface CircularPercents {
  repairAndMaintenance: number
  savedProducts: number
  buyBack: number
  rental: number
}

interface CardCircularProps {
  initialPercents: CircularPercents
}

const CardCircular = ({ initialPercents }: CardCircularProps) => {
  const { t } = useTranslation()
  const formatNumber = useFormatNumber()
  const {
    forecastInstoreCircular,
    setForecastInstoreRepair,
    setForecastInstoreSecondLife,
    setForecastInstoreBuyBack,
    setForecastInstoreRental,
    setCircularPercentTotal
  } = useContext(StoreTrajectoryContext)
  const isDisabled = forecastInstoreCircular == null || forecastInstoreCircular === 0

  const [percents, setPercents] = useState(initialPercents)

  const percentageTotal = sumSalesChannelPercents(percents)
  const isTotalMisallocated = !isDisabled && percentageTotal !== 100

  useEffect(() => {
    setCircularPercentTotal(percentageTotal)
  }, [percentageTotal, setCircularPercentTotal])

  useEffect(() => {
    if (isDisabled) {
      return
    }
    setForecastInstoreRepair(amountFromPercent(percents.repairAndMaintenance))
    setForecastInstoreSecondLife(amountFromPercent(percents.savedProducts))
    setForecastInstoreBuyBack(amountFromPercent(percents.buyBack))
    setForecastInstoreRental(amountFromPercent(percents.rental))
  }, [forecastInstoreCircular, isDisabled])

  const amountFromPercent = (percent: number | null) =>
    !isDisabled && percent != null ? (percent * forecastInstoreCircular) / 100 : 0

  const updatePercentAndValue =
    <K extends keyof CircularPercents>(propsKey: K, amountSetter: (value: number) => void) =>
    (newPercent: number) => {
      setPercents(prev => ({ ...prev, [propsKey]: newPercent }))
      amountSetter(amountFromPercent(newPercent))
    }

  return (
    <div className="mg-instore-gmv-segment-card" data-testid="instore-circular">
      <div className="mg-instore-gmv-segment-card-header">
        <div className="mg-instore-gmv-segment-card-header-title vp-subtitle-m">
          {t('general.trajectory.store.edit.instore_gmv_segment.circular.title')}
        </div>
        <div
          className={`mg-instore-gmv-segment-card-header-total vp-body-l ${isTotalMisallocated ? 'mg-instore-gmv-segment-card-header-total-error' : ''}`}
          data-testid="card-circular-total"
        >
          {formatNumber(percentageTotal / 100, '%', {
            style: 'percent',
            minimumFractionDigits: 0,
            maximumFractionDigits: 1,
            signDisplay: 'never'
          })}
        </div>
      </div>
      <AccountCard
        icon={<VpCircularityMaintainRepairIcon size={24} />}
        title={t('general.trajectory.store.recap.circular.repair')}
        amount={amountFromPercent(percents.repairAndMaintenance)}
        percent={percents.repairAndMaintenance ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('repairAndMaintenance', setForecastInstoreRepair)}
      />
      <AccountCard
        icon={<VpCircularitySecondLifeIcon size={24} />}
        title={t('general.trajectory.store.recap.circular.second-life')}
        amount={amountFromPercent(percents.savedProducts)}
        percent={percents.savedProducts ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('savedProducts', setForecastInstoreSecondLife)}
      />
      <AccountCard
        icon={<VpCircularityBuyBackIcon size={24} />}
        title={t('general.trajectory.store.recap.circular.buy-back')}
        amount={amountFromPercent(percents.buyBack)}
        percent={percents.buyBack ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('buyBack', setForecastInstoreBuyBack)}
      />
      <AccountCard
        icon={<VpCircularityShopRentalIcon size={24} />}
        title={t('general.trajectory.store.recap.circular.rental')}
        amount={amountFromPercent(percents.rental)}
        percent={percents.rental ?? 0}
        disabled={isDisabled}
        onPercentChange={updatePercentAndValue('rental', setForecastInstoreRental)}
      />
    </div>
  )
}

export default CardCircular
